import {
  Checkbox,
  IconButton,
  LoadingSkeleton,
  Row,
  Tooltip,
  TextInput,
} from "@narmi/design_system";
import React, { useState, useEffect, ChangeEvent, useCallback, useRef } from "react";
import type { ComponentProps } from "react";
import { NavigationAnchor } from "./navigation/NavigationLink";
import { NoResultsFound } from "./NoResultsFound";
import { AiIcon } from "./ai/AiIcon";
import styles from "./Table.module.css";
import cc from "classcat";

function TableComponent({
  children,
  kind = "default",
}: {
  children: React.ReactNode;
  kind?: "default" | "compact";
}) {
  return (
    <div
      className={cc({ [styles.table]: true, [styles.compact]: kind === "compact" })}
      role="table"
    >
      {children}
    </div>
  );
}

function TableQuery<T>({
  data,
  children,
  noResultsFallback = <NoResultsFound />,
}: {
  data: { results: T[]; count?: number } | null;
  children: (results: T[], count?: number) => React.ReactNode;
  noResultsFallback?: React.ReactNode;
}) {
  return (
    <LoadingSkeleton isLoading={data === null}>
      {data && data.results.length > 0 ? children(data.results, data.count) : noResultsFallback}
    </LoadingSkeleton>
  );
}

function TableSearch({
  label = "Search",
  initialSearchValue = "",
  endContent, // To support legacy pages using side drawer for filtering
  onDebouncedChange,
  aiSearchLink,
}: {
  label?: string;
  initialSearchValue?: string;
  endContent?: React.ReactNode;
  onDebouncedChange: (debouncedValue: string) => void;
  aiSearchLink?: string;
}) {
  const [search, setSearch] = useState(initialSearchValue);
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const onChange = useCallback(
    (value: string) => {
      clearTimeout(timeout.current!);
      timeout.current = setTimeout(() => {
        onDebouncedChange(value.trim());
      }, 500); // Debounce delay of 500ms
    },
    [onDebouncedChange]
  );

  // The name onDebouncedChange is misleading in this case, it is intended to let
  // consumers of this component know that the typical behavior is to debounce the change
  const makeUndebouncedChange = (value: string) => {
    clearTimeout(timeout.current!);
    setSearch(value);
    onDebouncedChange(value.trim());
  };

  useEffect(() => {
    setSearch(initialSearchValue);
  }, [initialSearchValue]);

  return (
    <TextInput
      startIcon="search"
      value={search}
      label={label}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        // fire off search immediately when user presses Enter
        if (e.key === "Enter") {
          makeUndebouncedChange(search);
        }
      }}
      onChange={(e: ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
        onChange(e.target.value);
      }}
      endContent={
        endContent ? (
          endContent
        ) : (
          // Default endContent is a clear button
          <Row gapSize="xxs" alignItems="center">
            {search && (
              <Row.Item>
                <IconButton textSize="l" name="x" onClick={() => makeUndebouncedChange("")} />
              </Row.Item>
            )}
            {aiSearchLink && (
              <Row.Item>
                <NavigationAnchor to={aiSearchLink}>
                  <AiIcon className={styles.aiIcon} />
                </NavigationAnchor>
              </Row.Item>
            )}
          </Row>
        )
      }
    />
  );
}

function TableHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.header} role="row">
      {children}
    </div>
  );
}

function TableHeaderCell({
  suffixIcon,
  onClick,
  children,
}: {
  suffixIcon?: ComponentProps<typeof IconButton>["name"];
  onClick?: () => void;
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLTableCellElement>) {
  if (suffixIcon) {
    return (
      <div className={styles.cell} onClick={onClick} role="columnheader">
        <Row gapSize="xxs" alignItems="center">
          <Row.Item shrink>{children}</Row.Item>
          <Row.Item shrink>
            <IconButton name={suffixIcon} />
          </Row.Item>
        </Row>
      </div>
    );
  }

  return (
    <div className={styles.cell} onClick={onClick} role="columnheader">
      {children}
    </div>
  );
}

function TableHeaderCheckbox(props: React.ComponentProps<typeof Checkbox>) {
  return (
    <div className={cc([styles.cell, styles.checkbox])} role="columnheader">
      <Checkbox {...props} />
    </div>
  );
}

function TableBody({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function TableRow({
  children,
  onClick,
  className,
  href,
}: React.PropsWithChildren<{
  className?: string;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
}>) {
  const classes = cc({
    [styles.row]: true,
    [className ?? ""]: !!className,
    [styles.clickable]: onClick || href,
  });
  if (href) {
    return (
      <NavigationAnchor to={href} onClick={onClick} className={classes} role="row">
        {children}
      </NavigationAnchor>
    );
  }
  return (
    <div onClick={onClick} className={classes} role="row">
      {children}
    </div>
  );
}

function TableCell({
  children,
  className,
  tooltipText,
}: {
  children: React.ReactNode;
  className?: string;
  tooltipText?: string;
}) {
  const [isTooltipOpen, setTooltipOpen] = React.useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  let resolvedTooltipText: string | undefined = tooltipText;

  /* The default behavior is to render children as the tooltip's content.
  If children is nested JSX other than a string or array of strings
  then no tooltip will be rendered by default and the caller should
  provide custom tooltipText to render a tooltip on hover. */
  if (tooltipText === undefined) {
    if (typeof children === "string") {
      resolvedTooltipText = children;
    }
    if (Array.isArray(children) && children.every((item) => typeof item === "string")) {
      // Example, Under the hood in JSX:
      // {user.first_name} {user.last_name} did this thing turns into
      // => ["Christian", "Paz", "did this thing"], so we should just join the array via ""
      resolvedTooltipText = children.join("");
    }
  }

  const handleMouseEnter = () => {
    if (resolvedTooltipText && contentRef.current) {
      // Only show tooltip if content is overflowing (ellipsized)
      const isOverflowing = contentRef.current.scrollWidth > contentRef.current.clientWidth;
      if (isOverflowing) {
        setTooltipOpen(true);
      }
    }
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
  };

  if (resolvedTooltipText) {
    return (
      <div
        className={styles.cell}
        role="cell"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleMouseLeave} // Close tooltip on click
      >
        <Tooltip text={resolvedTooltipText} isOpen={isTooltipOpen} wrapperDisplay="flex">
          <div ref={contentRef} className={className}>
            {children}
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <div
      // The className is intended to control the lowest level box in the DOM. If there's
      // a tooltip, we want to add the className to the wrapper div of its children
      className={cc({
        [styles.cell]: true,
        [className ?? ""]: !!className,
      })}
      role="cell"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleMouseLeave} // Close tooltip on click
    >
      {children}
    </div>
  );
}

function TableCheckbox({ onChange = () => {}, ...props }: React.ComponentProps<typeof Checkbox>) {
  return (
    <div
      className={cc([styles.cell, styles.checkbox])}
      // something strange is happening with the NDS handler,
      // it seems to conflict with the handler on the <tr>,
      // so stop event from bubbling up to <tr>.
      onClick={(e: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => {
        e.stopPropagation();
      }}
      role="cell"
    >
      <Checkbox
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChange(e);
        }}
        {...props}
      />
    </div>
  );
}

function ColGroup(props: React.PropsWithChildren<React.HTMLAttributes<HTMLTableColElement>>) {
  return (
    <div {...props} style={{ ...props.style, display: "table-column-group" }}>
      {props.children}
    </div>
  );
}

function Col({
  width,
  ...props
}: React.PropsWithChildren<React.HTMLAttributes<HTMLTableColElement>> & {
  width?: string;
}) {
  const colWidthStyle = width ? { width } : {};
  return <div {...props} style={{ ...props.style, ...colWidthStyle, display: "table-column" }} />;
}

TableComponent.Header = TableHeader;
TableComponent.HeaderCell = TableHeaderCell;
TableComponent.HeaderCheckbox = TableHeaderCheckbox;
TableComponent.Body = TableBody;
TableComponent.Row = TableRow;
TableComponent.Cell = TableCell;
TableComponent.Checkbox = TableCheckbox;
TableComponent.ColGroup = ColGroup;
TableComponent.Col = Col;
TableComponent.Query = TableQuery;
TableComponent.Search = TableSearch;

export const Table = TableComponent;
